import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = '/api/chat/admin';

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
};

const GROUP_STYLES = {
    A: { bg: '#cce5ff', color: '#004085' },
    B: { bg: '#d4edda', color: '#155724' },
    C: { bg: '#fff3cd', color: '#856404' },
};

const GROUP_COLORS = { A: '#4a90d9', B: '#50c878', C: '#f5a623', Unknown: '#adb5bd' };

const AbGroupBadge = ({ group }) => {
    if (!group) return null;
    const style = GROUP_STYLES[group] || { bg: '#e2e3e5', color: '#383d41' };
    return (
        <span style={{
            display: 'inline-block',
            fontSize: '10px',
            fontWeight: 'bold',
            padding: '1px 5px',
            borderRadius: '3px',
            backgroundColor: style.bg,
            color: style.color,
            marginLeft: '4px'
        }}>
            {group}
        </span>
    );
};

const PieChart = ({ data, size = 140 }) => {
    const total = data.reduce((sum, d) => sum + d.value, 0);
    if (total === 0) {
        return (
            <svg width={size} height={size}>
                <circle cx={size / 2} cy={size / 2} r={size / 2 - 4} fill="#e9ecef" />
                <text x={size / 2} y={size / 2 + 5} textAnchor="middle" fontSize="11" fill="#999">No data</text>
            </svg>
        );
    }

    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 4;

    let startAngle = -Math.PI / 2;
    const slices = data.map((d) => {
        const angle = (d.value / total) * 2 * Math.PI;
        const endAngle = startAngle + angle;
        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        const largeArc = angle > Math.PI ? 1 : 0;
        const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        const slice = { ...d, path };
        startAngle = endAngle;
        return slice;
    });

    return (
        <svg width={size} height={size}>
            {slices.map((s, i) => (
                <path key={i} d={s.path} fill={s.color} stroke="white" strokeWidth="1" />
            ))}
        </svg>
    );
};

const MetricCard = ({ title, data }) => {
    const chartData = data.map(g => ({ label: `Group ${g.group}`, value: g.value, color: GROUP_COLORS[g.group] || '#adb5bd' }));
    const total = data.reduce((sum, g) => sum + g.value, 0);

    return (
        <div style={{ textAlign: 'center', padding: '0 16px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>{title}</div>
            <PieChart data={chartData} size={130} />
            <div style={{ marginTop: '8px' }}>
                {data.map(g => (
                    <div key={g.group} style={{ fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: GROUP_COLORS[g.group] || '#adb5bd' }} />
                        <span>Group {g.group}: {g.value} {total > 0 ? `(${Math.round(g.value / total * 100)}%)` : ''}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AnalyticsTab = () => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const loadMetrics = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);

        fetch(`${API_BASE}/metrics?${params.toString()}`)
            .then(r => r.json())
            .then(data => {
                setMetrics(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [startDate, endDate]);

    useEffect(() => { loadMetrics(); }, [loadMetrics]);

    const groups = metrics?.byGroup || [];

    const sessionData = groups.map(g => ({ group: g.group, value: g.sessionCount }));
    const positiveData = groups.map(g => ({ group: g.group, value: g.positiveFeedbackCount }));
    const negativeData = groups.map(g => ({ group: g.group, value: g.negativeFeedbackCount }));

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'flex-end' }}>
                <div>
                    <div style={{ fontSize: '12px', marginBottom: '2px' }}>Start Date</div>
                    <input type="date" className="form-control form-control-sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div>
                    <div style={{ fontSize: '12px', marginBottom: '2px' }}>End Date</div>
                    <input type="date" className="form-control form-control-sm" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={loadMetrics}>Refresh</button>
            </div>

            {loading ? (
                <div style={{ color: '#999', textAlign: 'center', padding: '40px' }}>Loading...</div>
            ) : groups.length === 0 ? (
                <div style={{ color: '#999', textAlign: 'center', padding: '40px' }}>No data available.</div>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
                        {groups.map(g => (
                            <div key={g.group} style={{
                                border: '1px solid #dee2e6',
                                borderLeft: `4px solid ${GROUP_COLORS[g.group] || '#adb5bd'}`,
                                borderRadius: '6px',
                                padding: '12px 20px',
                                minWidth: '140px',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Group {g.group}{g.group === 'C' ? ' (Both)' : ''}</div>
                                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{g.sessionCount}</div>
                                <div style={{ fontSize: '11px', color: '#666' }}>sessions</div>
                                <div style={{ fontSize: '11px', marginTop: '8px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
                                    <span style={{ color: '#28a745' }}>+{g.positiveFeedbackCount}</span>
                                    <span style={{ color: '#dc3545' }}>-{g.negativeFeedbackCount}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: '32px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <MetricCard title="Sessions by Group" data={sessionData} />
                        <MetricCard title="Positive Feedback by Group" data={positiveData} />
                        <MetricCard title="Negative Feedback by Group" data={negativeData} />
                    </div>
                </>
            )}
        </div>
    );
};

const SessionList = ({ sessions, selectedSessionId, onSelect, filters, onFilterChange, totalCount, page, pageSize, onPageChange }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                <h5 style={{ margin: '0 0 10px' }}>Filters</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Account Number"
                        value={filters.accountNumber || ''}
                        onChange={(e) => onFilterChange({ ...filters, accountNumber: e.target.value })}
                    />
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.startDate || ''}
                            onChange={(e) => onFilterChange({ ...filters, startDate: e.target.value })}
                        />
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.endDate || ''}
                            onChange={(e) => onFilterChange({ ...filters, endDate: e.target.value })}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '12px' }}>
                            <input
                                type="checkbox"
                                checked={filters.hasPositiveFeedback || false}
                                onChange={(e) => onFilterChange({ ...filters, hasPositiveFeedback: e.target.checked || undefined })}
                            /> Positive
                        </label>
                        <label style={{ fontSize: '12px' }}>
                            <input
                                type="checkbox"
                                checked={filters.hasNegativeFeedback || false}
                                onChange={(e) => onFilterChange({ ...filters, hasNegativeFeedback: e.target.checked || undefined })}
                            /> Negative
                        </label>
                    </div>
                    <select
                        className="form-control form-control-sm"
                        value={filters.resolutionStatus || ''}
                        onChange={(e) => onFilterChange({ ...filters, resolutionStatus: e.target.value || undefined })}
                    >
                        <option value="">All Statuses</option>
                        <option value="Pending">Pending</option>
                        <option value="NoResolutionNeeded">No Resolution Needed</option>
                        <option value="Resolved">Resolved</option>
                    </select>
                </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
                {sessions.map((s) => (
                    <div
                        key={s.sessionId}
                        onClick={() => onSelect(s.sessionId)}
                        style={{
                            padding: '10px 12px',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer',
                            backgroundColor: s.sessionId === selectedSessionId ? '#e8f0fe' : 'white'
                        }}
                    >
                        <div style={{ fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                            {s.accountNumber}{s.studentName ? ` ${s.studentName}` : ''}
                            <AbGroupBadge group={s.abGroup} />
                        </div>
                        {s.courseName && (
                            <div style={{ fontSize: '11px', color: '#495057', fontStyle: 'italic' }}>{s.courseName}</div>
                        )}
                        <div style={{ fontSize: '11px', color: '#666' }}>{formatDate(s.createdUtc)}</div>
                        <div style={{ fontSize: '11px', display: 'flex', gap: '8px', marginTop: '4px' }}>
                            <span>{s.messageCount} msgs</span>
                            {s.positiveFeedbackCount > 0 && <span style={{ color: '#28a745' }}>+{s.positiveFeedbackCount}</span>}
                            {s.negativeFeedbackCount > 0 && <span style={{ color: '#dc3545' }}>-{s.negativeFeedbackCount}</span>}
                            <span style={{
                                fontSize: '10px',
                                padding: '1px 6px',
                                borderRadius: '3px',
                                backgroundColor: s.resolutionStatus === 'Resolved' ? '#d4edda' :
                                    s.resolutionStatus === 'NoResolutionNeeded' ? '#e2e3e5' : '#fff3cd',
                                color: s.resolutionStatus === 'Resolved' ? '#155724' :
                                    s.resolutionStatus === 'NoResolutionNeeded' ? '#383d41' : '#856404'
                            }}>
                                {s.resolutionStatus}
                            </span>
                        </div>
                    </div>
                ))}
                {sessions.length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No sessions found</div>
                )}
            </div>
            {totalCount > pageSize && (
                <div style={{ padding: '8px 12px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={page <= 1}
                        onClick={() => onPageChange(page - 1)}
                    >Prev</button>
                    <span style={{ fontSize: '12px' }}>Page {page} of {Math.ceil(totalCount / pageSize)}</span>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={page * pageSize >= totalCount}
                        onClick={() => onPageChange(page + 1)}
                    >Next</button>
                </div>
            )}
        </div>
    );
};

const SessionDetail = ({ sessionId }) => {
    const [detail, setDetail] = useState(null);
    const [resolution, setResolution] = useState({ status: 'Pending', ticketReference: '', resolutionNotes: '', changeReason: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!sessionId) return;
        fetch(`${API_BASE}/sessions/${sessionId}`)
            .then(r => r.json())
            .then(data => {
                setDetail(data);
                if (data.resolution) {
                    setResolution({
                        status: data.resolution.status || 'Pending',
                        ticketReference: data.resolution.ticketReference || '',
                        resolutionNotes: data.resolution.resolutionNotes || '',
                        changeReason: ''
                    });
                } else {
                    setResolution({ status: 'Pending', ticketReference: '', resolutionNotes: '', changeReason: '' });
                }
            });
    }, [sessionId]);

    const saveResolution = async () => {
        setSaving(true);
        try {
            await fetch(`${API_BASE}/sessions/${sessionId}/resolution`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(resolution)
            });
            setResolution(prev => ({ ...prev, changeReason: '' }));
        } catch (err) {
            console.error('Failed to save resolution:', err);
        } finally {
            setSaving(false);
        }
    };

    if (!detail) {
        return <div style={{ padding: '20px', color: '#999' }}>Select a session to view details</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {detail.accountNumber}{detail.studentName ? ` ${detail.studentName}` : ''}
                    <AbGroupBadge group={detail.abGroup} />
                </h5>
                {detail.courseName && (
                    <div style={{ fontSize: '12px', color: '#495057', fontStyle: 'italic', marginTop: '2px' }}>{detail.courseName}</div>
                )}
                <div style={{ fontSize: '12px', color: '#666' }}>
                    Started: {formatDate(detail.createdUtc)} | Last Activity: {formatDate(detail.lastActivityAt)}
                </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
                {detail.messages && detail.messages.map((msg) => {
                    if (msg.role === 'System') return null;
                    const isUser = msg.role === 'User';
                    return (
                        <div key={msg.id} style={{ marginBottom: '12px' }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: isUser ? 'flex-end' : 'flex-start'
                            }}>
                                <div style={{
                                    maxWidth: '80%',
                                    padding: '8px 12px',
                                    borderRadius: '10px',
                                    backgroundColor: isUser ? '#c41e3a' : '#f0f0f0',
                                    color: isUser ? 'white' : '#333',
                                    fontSize: '13px',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {msg.message}
                                </div>
                            </div>
                            <div style={{ fontSize: '10px', color: '#999', marginTop: '2px', textAlign: isUser ? 'right' : 'left' }}>
                                {formatDate(msg.createdUtc)}
                            </div>
                            {msg.rescheduleOptions && Array.isArray(msg.rescheduleOptions.slots) && msg.rescheduleOptions.slots.length > 0 && (
                                <div style={{ marginTop: '6px', maxWidth: '80%' }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Options presented · Class {msg.rescheduleOptions.classNumber} · {msg.rescheduleOptions.type}
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {msg.rescheduleOptions.slots.map((slot, i) => (
                                            <div key={i} style={{
                                                fontSize: '12px',
                                                border: '1px solid #c41e3a',
                                                color: '#c41e3a',
                                                borderRadius: '6px',
                                                padding: '5px 10px',
                                                backgroundColor: '#fff'
                                            }}>
                                                {slot.startDateTimeLocal}
                                                {slot.description ? <span style={{ color: '#888' }}> — {slot.description}</span> : null}
                                                {typeof slot.totalStudents === 'number' ? <span style={{ color: '#999', fontSize: '11px' }}> ({slot.totalStudents} students)</span> : null}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {msg.userFeedback && (
                                <div style={{ marginTop: '4px', textAlign: 'left' }}>
                                    <span style={{
                                        fontSize: '11px',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: msg.userFeedback === 'Positive' ? '#d4edda' : '#f8d7da',
                                        color: msg.userFeedback === 'Positive' ? '#155724' : '#721c24'
                                    }}>
                                        {msg.userFeedback === 'Positive' ? '\u{1F44D} Positive' : '\u{1F44E} Negative'}
                                    </span>
                                    {msg.userFeedbackMessage && (
                                        <div style={{
                                            marginTop: '4px',
                                            padding: '6px 10px',
                                            backgroundColor: '#f8d7da',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            color: '#721c24',
                                            borderLeft: '3px solid #dc3545'
                                        }}>
                                            {msg.userFeedbackMessage}
                                        </div>
                                    )}
                                </div>
                            )}
                            {msg.errorMessage && (
                                <div style={{
                                    marginTop: '4px',
                                    padding: '6px 10px',
                                    backgroundColor: '#fff3cd',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    color: '#856404',
                                    borderLeft: '3px solid #ffc107'
                                }}>
                                    Error: {msg.errorMessage}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div style={{ padding: '12px', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                <h6>Resolution</h6>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <select
                        className="form-control form-control-sm"
                        value={resolution.status}
                        onChange={(e) => setResolution({ ...resolution, status: e.target.value })}
                    >
                        <option value="Pending">Pending</option>
                        <option value="NoResolutionNeeded">No Resolution Needed</option>
                        <option value="Resolved">Resolved</option>
                    </select>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Ticket Reference"
                        value={resolution.ticketReference}
                        onChange={(e) => setResolution({ ...resolution, ticketReference: e.target.value })}
                    />
                    <textarea
                        className="form-control form-control-sm"
                        placeholder="Resolution Notes"
                        rows="2"
                        value={resolution.resolutionNotes}
                        onChange={(e) => setResolution({ ...resolution, resolutionNotes: e.target.value })}
                    />
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Reason for change"
                        value={resolution.changeReason}
                        onChange={(e) => setResolution({ ...resolution, changeReason: e.target.value })}
                    />
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={saveResolution}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Resolution'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SessionsTab = () => {
    const [sessions, setSessions] = useState([]);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 25;

    const loadSessions = useCallback(() => {
        const params = new URLSearchParams();
        params.set('page', page);
        params.set('pageSize', pageSize);
        if (filters.accountNumber) params.set('accountNumber', filters.accountNumber);
        if (filters.startDate) params.set('startDate', filters.startDate);
        if (filters.endDate) params.set('endDate', filters.endDate);
        if (filters.hasPositiveFeedback) params.set('hasPositiveFeedback', 'true');
        if (filters.hasNegativeFeedback) params.set('hasNegativeFeedback', 'true');
        if (filters.resolutionStatus) params.set('resolutionStatus', filters.resolutionStatus);

        fetch(`${API_BASE}/sessions?${params.toString()}`)
            .then(r => r.json())
            .then(data => {
                setSessions(data.sessions || []);
                setTotalCount(data.totalCount || 0);
            });
    }, [filters, page]);

    useEffect(() => { loadSessions(); }, [loadSessions]);
    useEffect(() => { setPage(1); }, [filters]);

    return (
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            <div style={{ width: '350px', borderRight: '1px solid #dee2e6', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <SessionList
                    sessions={sessions}
                    selectedSessionId={selectedSessionId}
                    onSelect={setSelectedSessionId}
                    filters={filters}
                    onFilterChange={setFilters}
                    totalCount={totalCount}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={setPage}
                />
            </div>
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <SessionDetail sessionId={selectedSessionId} />
            </div>
        </div>
    );
};

export const ChatReviewComponent = () => {
    const [activeTab, setActiveTab] = useState('sessions');

    const tabStyle = (tab) => ({
        padding: '8px 20px',
        cursor: 'pointer',
        borderBottom: activeTab === tab ? '2px solid #c41e3a' : '2px solid transparent',
        color: activeTab === tab ? '#c41e3a' : '#666',
        fontWeight: activeTab === tab ? 'bold' : 'normal',
        fontSize: '14px',
        userSelect: 'none'
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #dee2e6', borderRadius: '6px', height: 'calc(100vh - 150px)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                <div style={tabStyle('sessions')} onClick={() => setActiveTab('sessions')}>Sessions</div>
                <div style={tabStyle('analytics')} onClick={() => setActiveTab('analytics')}>Analytics</div>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
                {activeTab === 'sessions' ? <SessionsTab /> : <div style={{ overflowY: 'auto', height: '100%' }}><AnalyticsTab /></div>}
            </div>
        </div>
    );
};

export default ChatReviewComponent;
